import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { binService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Bin as BinType } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";

/**
 * Renders the Bin component.
 * This component fetches bin details based on the binId URL parameter and displays them.
 *
 * @returns {JSX.Element} The rendered Bin component.
 */
/**
 * React component that displays detailed information for a single bin.
 *
 * @returns {JSX.Element} The bin details component.
 */
export default function Bin() {
  const { t } = useTranslation();
  const { binId } = useParams();
  const handleNavigationError = useHandleNavigationError();

  // Get the single Bin from URL
  const { data: bin } = useQuery({
    queryKey: ["bin", binId],
    queryFn: () => {
      return binService.getSingle(binId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { bin: {} },
    retry: false,
    select: (data: { bin: BinType }) => {
      return data.bin;
    },
  });

  /**
   * Returns localized bin type label for a given bin type identifier.
   *
   * @param {number} binType - The numeric identifier of the bin type.
   * @returns {string} The localized string representing the bin type.
   */
  const getBinType = (binType: number) => {
    switch (binType) {
      case 1:
        return t("translation:bin");
      case 2:
        return t("translation:container");
      case 3:
        return t("translation:divisible_bin");
      case 4:
        return t("translation:cell");
      case 5:
        return t("translation:free_space");
      default:
        return "-";
    }
  };

  const basicInfo = [
    { label: t("translation:name"), value: bin?.name },
    { label: t("translation:type"), value: getBinType(bin?.type) },
    { label: t("translation:colour"), value: bin?.color },
    { label: t("translation:x_slots"), value: bin?.xSlots },
    { label: t("translation:y_slots"), value: bin?.ySlots },
  ];

  const physicalDimensions = [
    { label: t("translation:width"), value: bin?.width },
    { label: t("translation:depth"), value: bin?.depth },
    { label: t("translation:height"), value: bin?.height },
  ];

  const countInfo = [
    { label: t("translation:total_count"), value: bin?.totalCount },
    { label: t("translation:used_count"), value: bin?.usedCount },
    { label: t("translation:empty_count"), value: bin?.emptyCount },
    { label: t("translation:full_count"), value: bin?.fullCount },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{bin?.name}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:bins"), to: "/explorer/bins" },
          { label: bin?.name },
        ]}
      />
      <h1 className="text-[36px] font-medium">{bin?.name}</h1>
      <h2 className="text-[16px] mb-6 text-neutral-smoke">Bin Id: {bin?.id}</h2>
      <InfoList header={t("translation:basic_info")} items={basicInfo} />
      <InfoList
        header={t("translation:physical_dimensions")}
        items={physicalDimensions}
      />
      <InfoList header={t("translation:count_info")} items={countInfo} />
    </div>
  );
}
